import {
    BoxedAccordion,
    BoxedAccordionItem,
    ButtonLink,
    ButtonPrimary,
    Checkbox,
    Divider,
    IconCancelRegular,
    IconCheckedRegular,
    IconChevronLeftRegular,
    NavigationBar,
    Row,
    RowList,
    skinVars,
    Spinner,
    Text1,
    Text2,
    Title2,
    useWindowSize,
} from '@telefonica/mistica';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import { useEffect, useState } from 'react';
import { billsByMonth, monthsRange } from '../utils/constants/bills';
import { useBillsContext } from '../../../utils/store/context/bills-context/BillsContext';
import { formatPrices } from '../../../utils/dataTransformation/unitsConversion';
import { getFormatDate } from '../../../utils/dataTransformation/dateConversions';
import { showSnackbar } from '../../../utils/store/features/user';
import useUserlight from '../../../utils/hooks/useUserLight';
import { useDispatch } from 'react-redux';
import useBillsHook from '../utils/hooks';
import { IBill } from '../../../models/Bills.interfaces';
import { getBills } from '../../../services/BillService';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

const BillsDownload = () => {
    const size = useWindowSize();
    const isLight = useUserlight();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        bills,
        billsToDownload,
        isDownloadCompleted,
        billsWithError,
        billsDownloaded,
        billsZip,
        setBillsToDownload,
        setBills,
        setBillsDownloaded,
        setBillsWithError,
        setIsDownloadCompleted,
        setDownloadError,
    } = useBillsContext();
    const { sendToNative, downloadMultiplePdf } = useBillsHook();
    const { trackingUserInteraction } = new TrackingEvents();

    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedRange, setSelectedRange] = useState(6);
    const [isDownloading, setIsDownloading] = useState(false);
    const [billsInRange, setBillsInRange] = useState<IBill[]>([]);

    useEffect(() => {
        document.documentElement.scrollTop = 0;

        resetData();
        setBillsToDownload(new Set());
    }, []);

    useEffect(() => {
        if (bills.length === 0) getBillsHandler();
    }, []);

    useEffect(() => {
        getRangeBills(bills);
    }, [selectedRange]);

    const getBillsHandler = async () => {
        try {
            const response = await getBills(billsByMonth);
            const data = await response.data;

            setBills(data);
            getRangeBills(data);
        } catch (error) {
            console.log(error);
        }
    };

    const selectBillsHandler = () => setSelectionMode((prevState) => !prevState);

    const selectAllBillsHandler = () => setBillsToDownload(new Set(billsInRange.map((bill) => bill.id)));

    const changeBillsSelectionHandler = (billId: string) => {
        if (billsToDownload.has(billId))
            // @ts-ignore
            setBillsToDownload(() => {
                const newSet = new Set(billsToDownload);
                newSet.delete(billId);
                return newSet;
            });
        else setBillsToDownload(new Set(billsToDownload).add(billId));
    };

    const downloadBillsHandler = async () => {
        setIsDownloading(true);
        setSelectionMode(false);

        trackingUserInteraction('download_bills_tapped');
        await downloadMultiplePdf();
    };

    const downloadZipHandler = () => {
        if (!billsZip) return;

        const url = URL.createObjectURL(billsZip);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${t('invoice')}.zip`;
        link.click();

        URL.revokeObjectURL(url);

        navigate('/bills-download/feedback');
    };

    const navigationBackHandler = () => {
        resetData();
        if (selectionMode) setSelectionMode(false);
        else if (isDownloading) setIsDownloading(false);
        else {
            setBillsToDownload(new Set());
            navigate(-1);
        }
    };

    const changeRangeHandler = (checked: boolean, range: number) => {
        if (!checked) return;
        setSelectedRange(range);
        (document.querySelector('.bills-download__dropdown button') as HTMLDivElement)?.click();
    };

    const getRangeBills = (billsData: IBill[]) => {
        const timeLimit = new Date();
        timeLimit.setMonth(timeLimit.getMonth() - selectedRange);

        const tempBillsInRange = billsData.filter((bill) => new Date(bill.issueDate) >= timeLimit);

        setBillsInRange(tempBillsInRange);
    };

    const resetData = () => {
        setBillsDownloaded([]);
        setBillsWithError([]);
        setIsDownloadCompleted(false);
        setDownloadError(null);
    };

    return (
        <div className='bills-download'>
            <div
                className='bills-download__header'
                style={{
                    background: ' #FFFFFF',
                    position: size.width >= 1024 ? 'static' : 'sticky',
                }}
            >
                {!(isWebviewAndroid || isWebviewIOS) && (
                    <>
                        <div className='bills-download__header__navigationBar'>
                            <NavigationBar
                                withBorder={false}
                                onBack={navigationBackHandler}
                                title={t('downloadInvoices')}
                            />
                        </div>
                        <div className='bills-download__header__mobile-return'>
                            <div className='bills-download__header__mobile-return-cursor'>
                                <div
                                    className='bills-download__header__mobile-return-icon'
                                    onClick={() => {
                                        navigationBackHandler();
                                    }}
                                >
                                    <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.textPrimary}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>{' '}
                        </div>
                    </>
                )}

                <div className='bills-download__breadcrumbs'>
                    <BreadCrumbsWrapper
                        title={!isDownloading ? t('downloadInvoices') : t('summaryBillsDownload')}
                        breadcrumbs={
                            !isDownloading
                                ? [{ url: '/bills', title: t('invoice') }]
                                : [
                                      { url: '/bills', title: t('invoice') },
                                      { url: '/bills-download', title: t('downloadInvoices') },
                                  ]
                        }
                    />
                </div>
            </div>
            <div className='bills-download__divider'>
                <Divider />
            </div>
            <div className='bills-download__body'>
                {!isDownloading && (
                    <>
                        <div className='bills-download__selection'>
                            <Text1 color={skinVars.colors.textSecondary} weight='medium'>{`${billsToDownload.size} ${t(
                                'billsSelected',
                            ).toUpperCase()}`}</Text1>
                            <ButtonLink onPress={selectionMode ? selectAllBillsHandler : selectBillsHandler}>
                                {t(selectionMode ? 'billsAllSelection' : 'billsMultipleSelection')}
                            </ButtonLink>
                        </div>
                        <div className='bills-download__dropdown'>
                            <BoxedAccordion>
                                <BoxedAccordionItem
                                    title={t('rangeSelection')}
                                    subtitle={`${t('rangeSelected').split(' ')[0]} ${selectedRange} ${
                                        t('rangeSelected').split(' ')[1]
                                    }`}
                                    content={
                                        <RowList>
                                            {monthsRange.map((range, index) => (
                                                <Row
                                                    key={index}
                                                    asset={
                                                        <Checkbox
                                                            onChange={(e) => changeRangeHandler(e, range)}
                                                            checked={range === selectedRange}
                                                            name={range.toString()}
                                                        >{`${t('rangeSelected').split(' ')[0]} ${range} ${
                                                            t('rangeSelected').split(' ')[1]
                                                        }`}</Checkbox>
                                                    }
                                                    title=''
                                                />
                                            ))}
                                        </RowList>
                                    }
                                />
                            </BoxedAccordion>
                        </div>
                        <div className='bills-download__list'>
                            {!selectionMode && (
                                <RowList>
                                    {billsInRange.map((bill) => {
                                        return (
                                            <Row
                                                key={bill.id}
                                                title={`${t('billIssueDate')} ${getFormatDate(
                                                    bill.issueDate,
                                                    'DD/MM/YYYY',
                                                )}`}
                                                description={bill.id}
                                                detail={formatPrices(bill?.amount, 'spent')}
                                                withChevron
                                                onPress={() => {
                                                    if (isLight) {
                                                        dispatch(showSnackbar());
                                                    } else {
                                                        sendToNative(
                                                            '/bills/bill-detail',
                                                            'modeFlow-backgroundBlue',
                                                            `${bill.legalEntity}`,
                                                            bill.id,
                                                        );
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </RowList>
                            )}
                            {selectionMode && (
                                <RowList>
                                    {billsInRange.map((bill) => {
                                        return (
                                            <Row
                                                key={bill.id}
                                                title={`${t('billIssueDate')} ${getFormatDate(
                                                    bill.issueDate,
                                                    'DD/MM/YYYY',
                                                )}`}
                                                description={bill.id}
                                                detail={formatPrices(bill?.amount, 'spent')}
                                                checkbox={{
                                                    value: billsToDownload.has(bill.id),
                                                    onChange: () => changeBillsSelectionHandler(bill.id),
                                                }}
                                            />
                                        );
                                    })}
                                </RowList>
                            )}
                        </div>
                    </>
                )}
                {isDownloading && (
                    <>
                        <div className='bills-download__title'>
                            <Title2>
                                {isDownloadCompleted ? t('billsDownloadingTitle2') : t('billsDownloadingTitle1')}
                            </Title2>
                        </div>
                        <div className='bills-download__subtitle'>
                            <Title2>
                                {isDownloadCompleted
                                    ? billsWithError.length > 0
                                        ? t('billsDownloadingSubTitle3')
                                        : t('billsDownloadingSubTitle2')
                                    : t('billsDownloadingSubTitle1')}
                            </Title2>
                        </div>
                        <div className='bills-download__list bills-download__list--downloading'>
                            <RowList>
                                {billsInRange
                                    .filter((bill) => billsToDownload.has(bill.id))
                                    .map((bill) => {
                                        return (
                                            <Row
                                                key={bill.id}
                                                title={`${t('billIssueDate')} ${getFormatDate(
                                                    bill.issueDate,
                                                    'DD/MM/YYYY',
                                                )}`}
                                                description={bill.id}
                                                right={
                                                    billsDownloaded.includes(bill.id) ? (
                                                        <IconCheckedRegular color={skinVars.colors.success} />
                                                    ) : billsWithError.includes(bill.id) ? (
                                                        <IconCancelRegular color={skinVars.colors.textError} />
                                                    ) : (
                                                        <Spinner size={24} />
                                                    )
                                                }
                                            />
                                        );
                                    })}
                            </RowList>
                        </div>
                    </>
                )}
            </div>
            {(selectionMode || isDownloading) && (
                <div className='bills-download__bottom'>
                    <ButtonPrimary
                        disabled={billsToDownload.size <= 0}
                        role='onPress'
                        onPress={!isDownloading ? downloadBillsHandler : downloadZipHandler}
                        loadingText={t('downloading')}
                    >
                        {!isDownloading ? t('billsDownloadSelection') : t('save')}
                    </ButtonPrimary>
                </div>
            )}
        </div>
    );
};

export default BillsDownload;
